// Central place for API base URL + all data-fetching functions.
//
// Wired up to the real Today Talks backend:
//   - GET /categories            -> live category list (filtered to status=1)
//   - GET /todattalkspost        -> every post across every category, in one call
//   - GET /todattalkspost/:id    -> a single post's full details
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://todaytalks.mpdatahub.com/api";

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes - articles don't change that fast

/**
 * Generic fetch wrapper - handles JSON parsing + error normalization.
 */
async function request(endpoint) {
  const res = await fetch(`${BASE_URL}${endpoint}`);
  if (!res.ok) {
    throw new Error(`Request failed (${res.status}) for ${endpoint}`);
  }
  const json = await res.json();
  if (json && json.success === false) {
    throw new Error(json.message || `Request failed for ${endpoint}`);
  }
  return json;
}

// ---- category slug helper --------------------------------------------------------
// Categories now come from the live /categories endpoint (no more hardcoded
// list), so slugs are derived from the category name instead of a fixed
// config entry. Used both when normalizing the category list itself and
// when reading the category embedded on each post, so the two always agree.
function slugify(name) {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ---- tag parsing --------------------------------------------------------
// The backend sometimes stores tags as a clean JSON array, sometimes as a
// JSON array that's been JSON.stringify'd multiple times over (so it shows
// up as escaped quotes nested several levels deep), and sometimes as a
// plain comma-separated string. This unwraps whichever shape shows up.
function unwrapJsonArray(value, maxDepth = 5) {
  let current = value;
  for (let i = 0; i < maxDepth; i += 1) {
    if (Array.isArray(current)) return current;
    if (typeof current !== "string") return null;
    const trimmed = current.trim();
    if (!trimmed.startsWith("[")) return null;
    try {
      current = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return Array.isArray(current) ? current : null;
}

function parseTags(raw) {
  if (!raw) return [];

  let value = unwrapJsonArray(raw);
  if (value === null) {
    // Not JSON at all - treat as a comma-separated list.
    value = typeof raw === "string" ? raw.split(",") : [];
  }

  const flat = [];
  value.forEach((entry) => {
    if (typeof entry !== "string") return;
    const nested = unwrapJsonArray(entry);
    if (nested) {
      nested.forEach((n) => typeof n === "string" && flat.push(n));
    } else {
      flat.push(entry);
    }
  });

  const cleaned = flat
    .map((tag) =>
      tag
        .replace(/^\[+|\]+$/g, "")
        .replace(/^"+|"+$/g, "")
        .trim(),
    )
    .filter((tag) => tag && tag !== "[]" && tag.length < 40);

  return Array.from(new Set(cleaned)).slice(0, 6);
}

// ---- date helpers --------------------------------------------------------
function formatTimeAgo(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr${diffHr > 1 ? "s" : ""} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  const diffWeek = Math.floor(diffDay / 7);
  if (diffWeek < 5) return `${diffWeek} week${diffWeek > 1 ? "s" : ""} ago`;
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatPublishedDate(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ---- content helpers -------------------------------------------------
function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// The API returns article bodies as plain text with \r\n line breaks, not
// HTML. Turn that into paragraphs so it reads well on the details page.
function textToParagraphs(text) {
  if (!text) return "";
  return text
    .split(/\r\n\r\n|\n\n|\r\r/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map(
      (block) => `<p>${escapeHtml(block).replace(/\r\n|\n|\r/g, "<br/>")}</p>`,
    )
    .join("");
}

function truncate(text, maxLength) {
  if (!text || text.length <= maxLength) return text || "";
  return `${text.slice(0, maxLength).trim()}…`;
}

// ---- categories --------------------------------------------------------
let categoriesCache = null; // { data: NormalizedCategory[], ts: number }

async function fetchCategoriesRaw() {
  if (categoriesCache && Date.now() - categoriesCache.ts < CACHE_TTL_MS)
    return categoriesCache.data;

  const json = await request("/categories");
  const rawItems = Array.isArray(json.data) ? json.data : [];

  const items = rawItems
    .filter((c) => c && (c.status === 1 || c.status === true))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((c) => ({
      id: c.id,
      name: c.name,
      slug: slugify(c.name),
      image: c.image_url || c.image || "",
    }));

  categoriesCache = { data: items, ts: Date.now() };
  return items;
}

/**
 * Live category list from /categories, filtered to active (status=1) ones.
 * `subcategories: []` is kept on each entry since TopBar/Footer render
 * against that shape - this backend has no subcategory concept.
 */
export async function fetchCategories() {
  const cats = await fetchCategoriesRaw();
  return cats.map((c) => ({ ...c, subcategories: [] }));
}

// ---- normalization --------------------------------------------------------
// Posts from /todattalkspost (and /todattalkspost/:id) look like:
// { id, title, summary, description, tags, imageUrl, status, trending,
//   likes_count, createdAt, category: { id, name, image, status } }
// This maps that onto the shape the UI components already expect, and
// keeps the raw record around so the details page can build a full
// article view without a second network call.
function normalizePost(raw, fallback = {}) {
  const id = raw.id ?? raw.ID ?? raw._id;
  const title = raw.title || "Untitled";
  const summary = raw.summary || "";
  const description = raw.description || summary || "";
  const createdAt = raw.createdAt || raw.created_at || "";

  const embeddedCategory =
    raw.category && raw.category.name ? raw.category : null;

  const isTrending = fallback.label === "Trending";

  const category = isTrending
    ? raw?.category || ""
    : embeddedCategory
      ? embeddedCategory.name
      : fallback.label || "";

  const categorySlug = isTrending
    ? raw
      ? slugify(raw.category)
      : ""
    : embeddedCategory
      ? slugify(embeddedCategory.name)
      : fallback.slug || "";

  return {
    id: String(id),
    title,
    description: truncate(summary || description.split(/\r?\n/)[0] || "", 160),
    image: raw.imageUrl || raw.image || "",
    category,
    categorySlug,
    timeAgo: formatTimeAgo(createdAt),
    isVideo: false,
    trending: raw.trending === 1 || raw.trending === true,
    tags: parseTags(raw.tags),
    createdAt,
    _raw: raw,
  };
}

function toDetail(article) {
  const raw = article._raw || {};
  const subtitle = raw.summary && raw.summary !== raw.title ? raw.summary : "";

  return {
    id: article.id,
    title: article.title,
    subtitle,
    image: article.image,
    imageCaption: "",
    author: "",
    publishedAt: formatPublishedDate(raw.createdAt || raw.created_at),
    category: article.category,
    categorySlug: article.categorySlug,
    content: textToParagraphs(raw.description || article.description),
    tags: article.tags,
  };
}

// ---- all posts: single endpoint + cache --------------------------------------------------------
// Every category's posts now come back from one call instead of 9 separate
// per-category requests. Category filtering (fetchNewsByCategory,
// fetchCategoryHighlights, ...) happens client-side over this cached list.
let allPostsCache = null; // { data: NormalizedArticle[], ts: number }

async function fetchAllPosts() {
  if (allPostsCache && Date.now() - allPostsCache.ts < CACHE_TTL_MS)
    return allPostsCache.data;

  const json = await request("/todattalkspost");
  const rawItems = Array.isArray(json.data) ? json.data : [];

  const items = rawItems
    .filter((item) => item && item.status !== 0 && item.status !== false)
    .map((item) => normalizePost(item))
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  allPostsCache = { data: items, ts: Date.now() };
  return items;
}

// ---- trending top 5 (separate endpoint, not tied to any category) --------
let trendingCache = null; // { data: NormalizedArticle[], ts: number }

async function fetchTrendingArticles() {
  if (trendingCache && Date.now() - trendingCache.ts < CACHE_TTL_MS)
    return trendingCache.data;

  const json = await request("/trending-top-five");
  const rawItems = Array.isArray(json.data) ? json.data : [];
  const items = rawItems
    .filter((item) => item && item.status !== 0 && item.status !== false)
    .map((item) =>
      normalizePost(item, { label: "Trending", slug: "trending" }),
    );

  trendingCache = { data: items, ts: Date.now() };
  return items;
}

/**
 * Top 5 trending articles for the home page marquee.
 */
export async function fetchTrendingTopFive() {
  return fetchTrendingArticles();
}

// ---- public API --------------------------------------------------------

/**
 * Home page feed: every post, newest first, paginated client-side over the
 * single cached /todattalkspost list.
 */
export async function fetchHomeNews({ page = 1, limit = 12 } = {}) {
  const all = await fetchAllPosts();
  const start = (page - 1) * limit;
  const items = all.slice(start, start + limit);
  const totalPages = Math.max(1, Math.ceil(all.length / limit));
  return { items, page, totalPages };
}

/**
 * Single category feed: filters the cached /todattalkspost list by the
 * category's slug (derived from the category name embedded on each post),
 * paginated client-side. "trending" is a pseudo-category that instead reads
 * from the separate /trending-top-five endpoint.
 */
export async function fetchNewsByCategory(
  categorySlug,
  { page = 1, limit = 12 } = {},
) {
  const all =
    categorySlug === "trending"
      ? await fetchTrendingArticles()
      : (await fetchAllPosts()).filter((a) => a.categorySlug === categorySlug);
  const start = (page - 1) * limit;
  const items = all.slice(start, start + limit);
  const totalPages = Math.max(1, Math.ceil(all.length / limit));
  return { items, page, totalPages };
}

/**
 * Single article, fetched fresh from the real post-details endpoint
 * (GET /todattalkspost/:id) rather than pulled out of a cached list.
 */
export async function fetchNewsById(_categorySlug, id) {
  const json = await request(`/todattalkspost/${id}`);
  const raw = Array.isArray(json.data) ? json.data[0] : json.data;
  if (!raw) throw new Error("Article not found");
  return toDetail(normalizePost(raw));
}

/**
 * Trending items + top tags for a category's sidebar widgets. Reuses the
 * same cached post list as fetchNewsByCategory, so this doesn't cost an
 * extra network request when called alongside it.
 */
export async function fetchCategoryHighlights(
  categorySlug,
  { trendingCount = 5, tagCount = 10 } = {},
) {
  const all = (await fetchAllPosts()).filter(
    (a) => a.categorySlug === categorySlug,
  );

  const trending = all.filter((a) => a.trending).slice(0, trendingCount);

  const tagCounts = new Map();
  all.forEach((article) => {
    article.tags.forEach((tag) => {
      tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
    });
  });
  const tags = Array.from(tagCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([tag]) => tag)
    .slice(0, tagCount);

  return { trending, tags };
}

/**
 * Search across the cached post list by title/description.
 */
export async function searchNews(query) {
  const q = query.trim().toLowerCase();
  if (!q) return { items: [] };
  const all = await fetchAllPosts();
  const items = all.filter(
    (a) =>
      a.title.toLowerCase().includes(q) ||
      a.description.toLowerCase().includes(q),
  );
  return { items };
}
